import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DocEditLinkService } from "@/services/api/DocEditLinkService";
import { DocEditLink } from "@/types/knowledge";

/**
 * Owner-side management of a document's public edit link:
 * generate / rotate, set-or-clear PIN, and revoke.
 */
export const useDocEditLink = (documentId?: string, enabled = true) => {
  const queryClient = useQueryClient();
  const queryKey = ["doc-edit-link", documentId];

  const query = useQuery<DocEditLink | null>({
    queryKey,
    queryFn: () => DocEditLinkService.get(documentId as string),
    enabled: !!documentId && enabled,
    staleTime: 60 * 1000,
  });

  const setLink = (link: DocEditLink | null) => queryClient.setQueryData(queryKey, link);

  const generate = useMutation({
    mutationFn: (rotate?: boolean) => DocEditLinkService.generate(documentId as string, rotate),
    onSuccess: (link, rotate) => {
      setLink(link);
      toast.success(rotate ? "New link generated — the old one no longer works" : "Edit link ready to share");
    },
    onError: () => toast.error("Couldn't create the edit link"),
  });

  const setPin = useMutation({
    mutationFn: (pin: string | null) => DocEditLinkService.setPin(documentId as string, pin),
    onSuccess: (link, pin) => {
      setLink(link);
      toast.success(pin ? "PIN set — it's now required to save" : "PIN removed");
    },
    onError: () => toast.error("Couldn't update the PIN"),
  });

  const revoke = useMutation({
    mutationFn: () => DocEditLinkService.revoke(documentId as string),
    onSuccess: () => {
      setLink(null);
      toast.success("Link revoked");
    },
    onError: () => toast.error("Couldn't revoke the link"),
  });

  return {
    link: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    generate,
    setPin,
    revoke,
  };
};
